<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Toast Notification -->
    <Toast
      :show="showToast"
      :message="toastMessage"
      :subtitle="toastSubtitle"
      :show-subtitle="true"
      :type="toastType" />

    <div class="flex-1 flex flex-col gap-5 container-padding pb-24 py-4">
      <!-- Current value inputs -->
      <div class="card-shell">
        <div class="space-y-3">
          <div>
            <div class="label mb-1">
              {{ $t('views.more.focalLength.focalLengthLabel') }}
            </div>
            <div class="flex items-center gap-2">
              <input
                v-model.number="focalLength"
                type="number"
                step="0.01"
                min="1"
                max="100"
                class="input-field flex-1" />
            </div>
          </div>

          <div>
            <div class="label mb-1">
              {{ $t('views.more.focalLength.focal35mmLabel') }}
            </div>
            <div class="flex items-center gap-2">
              <input
                v-model.number="focal35mm"
                type="number"
                step="0.1"
                min="1"
                max="500"
                class="input-field flex-1" />
            </div>
          </div>

          <div
            class="flex start pt-3 text-sm border-t border-[var(--color-primary-400)] gap-2">
            <span class="label">
              {{ $t('views.more.focalLength.sensorSizeLabel') }}
            </span>

            <span
              class="text-[var(--color-text)] font-semibold tabular-nums tracking-wide">
              {{ calculatedSensorSize.toFixed(2) }} mm
            </span>
          </div>

          <button
            v-if="hasChanges"
            @click="saveToLocalStorage"
            class="btn btn--primary w-full">
            {{ $t('common.buttons.save') }}
          </button>
        </div>
      </div>

      <!-- Detect button -->
      <div>
        <input
          type="file"
          ref="fileInput"
          accept="image/jpeg,image/jpg,image/png"
          class="hidden"
          @change="handleFileUpload" />
        <button
          @click="handleDetectClick"
          :disabled="isDetecting"
          class="focal-btn w-full flex items-center justify-center gap-2"
          :class="{ 'opacity-50 cursor-not-allowed': isDetecting }">
          <ScanSearch class="w-6 h-6" />
          <span>{{
            isDetecting
              ? $t('views.more.focalLength.detecting')
              : $t('views.more.focalLength.detect')
          }}</span>
        </button>

        <!-- HEIC Note -->
        <p class="label label--small mt-2 leading-relaxed">
          {{ $t('views.more.focalLength.heicNote') }}
        </p>
      </div>

      <!-- Preset buttons -->
      <div>
        <h3 class="label mb-3">
          {{ $t('views.more.focalLength.presets') }}
        </h3>

        <div class="grid grid-cols-2 gap-4">
          <template v-for="(preset, index) in presets" :key="preset.label">
            <button
              @click="selectPreset(preset)"
              :class="[
                selectedPresetLabel === preset.label
                  ? 'focal-btn-selected'
                  : 'focal-btn',
              ]">
              {{ preset.label }}
            </button>

            <!-- Divider after iPhone section -->
            <div
              v-if="index === 21"
              class="col-span-2 my-2 border-t border-[var(--color-primary-300)]"></div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { ScanSearch } from 'lucide-vue-next'
  import { Capacitor } from '@capacitor/core'
  import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
  import Toast from '@/components/ui/feedback/Toast.vue'
  import focalLengthPresetsData from '@/constants/focalLengthPresets.json'

  import { useI18n } from 'vue-i18n'
  import { useUiStore } from '@/stores/ui.store.js'
  import { useFocalLength } from '@/utils/ml/useFocalLength.js'
  import { ExifService } from '@/services/ml/ExifService.js'

  const { t } = useI18n()
  const uiStore = useUiStore()
  const {
    getDefaultFocalLength,
    getDefaultFocal35mm,
    setDefaultSensorSettings,
    DEFAULT_FOCAL_LENGTH,
    DEFAULT_FOCAL_35MM,
  } = useFocalLength()

  const exifService = new ExifService()

  onMounted(() => {
    uiStore.setHeader({
      title: t('views.more.focalLength.title'),
      showBack: true,
      showAdd: false,
    })
  })

  const fileInput = ref(null)

  // Load presets from JSON file
  const presets = focalLengthPresetsData

  const focalLength = ref(DEFAULT_FOCAL_LENGTH)
  const focal35mm = ref(DEFAULT_FOCAL_35MM)
  const savedFocalLength = ref(DEFAULT_FOCAL_LENGTH)
  const savedFocal35mm = ref(DEFAULT_FOCAL_35MM)
  const isDetecting = ref(false)
  const selectedPresetLabel = ref(null)

  const hasChanges = computed(() => {
    return (
      focalLength.value !== savedFocalLength.value ||
      focal35mm.value !== savedFocal35mm.value
    )
  })

  // Calculated sensor size (long side)
  const calculatedSensorSize = computed(() => {
    const f = focalLength.value
    const f35 = focal35mm.value || 0
    if (f > 0 && f35 > 0) {
      const diagonal = (43.27 * f) / f35
      return (4 * diagonal) / 5 // Assuming 4:3 sensor
    }
    return 4.2 // Default
  })

  // Toast notification state
  const showToast = ref(false)
  const toastMessage = ref('')
  const toastSubtitle = ref('')
  const toastType = ref('saved')

  function showToastNotification(message, type = 'saved', subtitleText = '') {
    showToast.value = false
    toastMessage.value = message
    toastType.value = type
    toastSubtitle.value = subtitleText
    setTimeout(() => {
      showToast.value = true
    }, 100)
  }

  function triggerFileSelect() {
    fileInput.value?.click()
  }

  async function handleDetectClick() {
    if (isDetecting.value) return

    // On native (iOS/Android), use Capacitor Camera prompt.
    // This avoids iOS "Take Photo" picker flows that often strip EXIF.
    if (Capacitor.isNativePlatform()) {
      await detectViaNativeCameraPrompt()
      return
    }

    // Web fallback: use file picker
    triggerFileSelect()
  }

  function applyDetectedExif(exifData) {
    if (!exifData?.focalLength) return false

    focalLength.value = parseFloat(exifData.focalLength.toFixed(2))

    // Only overwrite 35mm equivalent if it exists in EXIF (ExifService handles fallbacks)
    if (exifData.focalLengthIn35mm) {
      focal35mm.value = parseFloat(exifData.focalLengthIn35mm.toFixed(1))
    }

    selectedPresetLabel.value = null
    saveToLocalStorage()

    showToastNotification(
      t('views.more.focalLength.detected'),
      'saved',
      `${focalLength.value}mm (${focal35mm.value}mm eq.)`
    )
    return true
  }

  async function detectViaNativeCameraPrompt() {
    isDetecting.value = true
    try {
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera, // use native camera app
        resultType: CameraResultType.Uri,
        quality: 90,
        correctOrientation: false,
      })

      const webPath = photo?.webPath
      if (!webPath) {
        showToastNotification(
          t('views.more.focalLength.detectionFailedTitle'),
          'warning',
          t('views.more.focalLength.detectionFailed')
        )
        return
      }

      const response = await fetch(webPath)
      const blob = await response.blob()
      const file = new File([blob], 'photo.jpg', {
        type: blob.type || 'image/jpeg',
      })

      const exifData = await exifService.extractExifData(file)
      const applied = applyDetectedExif(exifData)
      if (!applied) {
        showToastNotification(
          t('views.more.focalLength.detectionFailedTitle'),
          'warning',
          t('views.more.focalLength.detectionFailed')
        )
      }
    } catch (err) {
      // User cancelled: do nothing (avoid scary error toast)
      const msg = String(err?.message || '')
      if (msg.toLowerCase().includes('cancel')) return

      console.error('Failed to extract EXIF (native prompt):', err)
      showToastNotification(
        t('views.more.focalLength.detectionFailedTitle'),
        'error',
        t('views.more.focalLength.detectionFailed')
      )
    } finally {
      isDetecting.value = false
    }
  }

  async function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return

    isDetecting.value = true
    try {
      const exifData = await exifService.extractExifData(file)

      const applied = applyDetectedExif(exifData)
      if (!applied) {
        showToastNotification(
          t('views.more.focalLength.detectionFailedTitle'),
          'warning',
          t('views.more.focalLength.detectionFailed')
        )
      }
    } catch (err) {
      console.error('Failed to extract EXIF:', err)
      showToastNotification(
        t('views.more.focalLength.detectionFailedTitle'),
        'error',
        t('views.more.focalLength.detectionFailed')
      )
    } finally {
      isDetecting.value = false
      if (event.target) event.target.value = '' // Reset input
    }
  }

  function selectPreset(preset) {
    focalLength.value = preset.focalLength
    focal35mm.value = preset.focal35mm
    selectedPresetLabel.value = preset.label
    savedFocalLength.value = preset.focalLength
    savedFocal35mm.value = preset.focal35mm
    saveToLocalStorage()
  }

  function saveToLocalStorage() {
    try {
      setDefaultSensorSettings(focalLength.value, focal35mm.value)
      savedFocalLength.value = focalLength.value
      savedFocal35mm.value = focal35mm.value
      showToastNotification(t('common.toasts.saved'), 'saved')
    } catch (err) {
      console.error('Failed to save settings:', err)
    }
  }

  function loadSettings() {
    try {
      focalLength.value = getDefaultFocalLength()
      focal35mm.value = getDefaultFocal35mm()
      savedFocalLength.value = focalLength.value
      savedFocal35mm.value = focal35mm.value

      // Try to find matching preset
      const matchingPreset = presets.find((p) => {
        return (
          Math.abs(p.focalLength - focalLength.value) < 0.01 &&
          Math.abs(p.focal35mm - focal35mm.value) < 0.5
        )
      })
      if (matchingPreset) {
        selectedPresetLabel.value = matchingPreset.label
      } else {
        selectedPresetLabel.value = null
      }
    } catch (err) {
      console.error('Failed to load settings:', err)
    }
  }

  watch(
    () => [focalLength.value, focal35mm.value],
    () => {
      // Only update preset highlight if user manually changed values (not from preset selection)
      if (
        focalLength.value === savedFocalLength.value &&
        focal35mm.value === savedFocal35mm.value
      ) {
        return // Values match saved state, no need to update
      }

      const matchingPreset = presets.find((p) => {
        return (
          Math.abs(p.focalLength - focalLength.value) < 0.01 &&
          Math.abs(p.focal35mm - focal35mm.value) < 0.5
        )
      })
      selectedPresetLabel.value = matchingPreset ? matchingPreset.label : null
    }
  )

  onMounted(() => {
    loadSettings()
  })
</script>
